import { ReactNode } from 'react';

import {
  ChakraFlex,
  ChakraText,
  Check,
  ClipboardList,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  UserPlus,
} from '@equip.health/ui';

import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';

import SurveyListItemActions from './SurveyListItemActions';

const { primary } = EquipUIFireflyV2Theme.colors;

const { label, paragraph } = EquipUITheme.typography;

const StatusToPrimaryTextMapper: Record<
  SurveyResponseStatus,
  (name?: string) => string
> = {
  [SurveyResponseStatus.NEW]: (name: string) => `${name} is a new patient`,
  [SurveyResponseStatus.REQUESTED]: (name: string) =>
    `${name}'s profile is incomplete`,
  [SurveyResponseStatus.INCOMPLETE]: (name: string) =>
    `${name}'s profile is incomplete`,
  [SurveyResponseStatus.IN_PROGRESS]: (name: string) =>
    `${name}'s profile is incomplete`,
  [SurveyResponseStatus.DONE]: (name: string) =>
    `${name}'s profile is complete`,
  [SurveyResponseStatus.VOID]: (_name: string) => '',
};

const StatusToSecondaryTextMapper: Record<SurveyResponseStatus, string> = {
  [SurveyResponseStatus.NEW]:
    "Please complete an intake survey to build this patient's profile",
  [SurveyResponseStatus.REQUESTED]:
    'An intake survey request has been sent, but the survey has not been started',
  [SurveyResponseStatus.INCOMPLETE]:
    "An intake survey has been started, but it isn't complete",
  [SurveyResponseStatus.IN_PROGRESS]:
    "An intake survey has been started, but it isn't complete",
  [SurveyResponseStatus.DONE]: 'Select an option to view intake survey results',
  [SurveyResponseStatus.VOID]: '',
};

const StatusToIconMapper: Record<SurveyResponseStatus, ReactNode> = {
  [SurveyResponseStatus.NEW]: <UserPlus />,
  [SurveyResponseStatus.REQUESTED]: <UserPlus />,
  [SurveyResponseStatus.INCOMPLETE]: <ClipboardList />,
  [SurveyResponseStatus.IN_PROGRESS]: <ClipboardList />,
  [SurveyResponseStatus.DONE]: (
    <Check fill={primary[100]} height="24px" width="24px" />
  ),
  [SurveyResponseStatus.VOID]: null,
};

type SurveyListItemProps = {
  isDisabled: boolean;
  onCTAClick: (survey: SurveyResponse, isPrimaryCTA: boolean) => void;
  onEditClick: (survey: SurveyResponse) => void;
  patientFirstName: string;
  survey: SurveyResponse;
};

const SurveyListItem = ({
  patientFirstName,
  survey,
  ...rest
}: SurveyListItemProps) => (
  <ChakraFlex alignItems="center" justifyContent="space-between" width="100%">
    <ChakraFlex alignItems="center" marginRight="8px">
      {StatusToIconMapper[survey.status]}
      <ChakraFlex direction="column" paddingLeft="24px">
        <ChakraText {...paragraph} marginBottom="-8px" marginTop="-4px">
          {StatusToPrimaryTextMapper[survey.status]?.(patientFirstName) ?? ''}
        </ChakraText>
        <ChakraText {...label} color="neutral.secondary">
          {StatusToSecondaryTextMapper[survey.status] ?? ''}
        </ChakraText>
      </ChakraFlex>
    </ChakraFlex>
    <SurveyListItemActions survey={survey} {...rest} />
  </ChakraFlex>
);

export default SurveyListItem;
